import { Avatar, Colors, defaultHover, Icon, Pills } from '@amelio/ui-library';

import type { MobileTreeViewProps } from '../../types';

import { UserCard } from '@/components/UserCard/UserCard';
import { useAmelioTranslations } from '@/hooks';
import { useCompanyInformation } from '@/hooks/company/useCompanyInformation';
import { getFullName, getInitials, getProfilePictureUrl } from '@/utils/user';

export const MobileTreeView = ({
    centerNode,
    satelliteNodes,
    onRemove,
    dataTestId,
}: MobileTreeViewProps) => {
    const { getJobTitle } = useCompanyInformation();
    const { t } = useAmelioTranslations();

    return (
        <div
            className={'bg-background w-full'}
            data-testid={dataTestId}
        >
            {/* Center Node (Reviewee) */}
            {centerNode && (
                <div className={'relative mb-8 flex flex-col items-center'}>
                    <Avatar
                        src={getProfilePictureUrl(centerNode.data?.profilePictureUrl || '')}
                        alt={centerNode.data?.id ?? ''}
                        borderColor={Colors.PRIMARY}
                        size={'lg'}
                        borderSize={'ring-4'}
                        initials={getInitials(centerNode.data)}
                        label={
                            <Pills
                                size={'sm'}
                                variant={'primary'}
                            >
                                {t('evaluation360AssignReviewers.assignReviewers.revieweeBadge')}
                            </Pills>
                        }
                    />

                    <div className={'bg-primary mt-2 h-12 w-0.5'} />
                </div>
            )}

            {/* Team Members Grid */}
            <div className={'px-4 pb-24'}>
                <div className={'grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3'}>
                    {satelliteNodes.map((satelliteNode) => (
                        <div
                            key={satelliteNode.data?.id}
                            data-testid={`satellite-card-${satelliteNode.data?.id}`}
                            className={
                                'border-primary flex flex-row items-center justify-between rounded-lg border py-2 shadow-sm'
                            }
                        >
                            <UserCard
                                alt={'user-card'}
                                avatar={
                                    <Avatar
                                        src={getProfilePictureUrl(
                                            satelliteNode.data?.profilePictureUrl || '',
                                        )}
                                        alt={satelliteNode.data?.id ?? ''}
                                        borderColor={Colors.PRIMARY}
                                        borderSize={'ring-2'}
                                        initials={getInitials(satelliteNode.data)}
                                        size={'sm'}
                                    />
                                }
                                description={
                                    <Pills
                                        size={'sm'}
                                        textColor={'text-secondary-700'}
                                        className={'mt-1 inline-flex self-start'}
                                    >
                                        <div className={'p-1'}>
                                            {getJobTitle(satelliteNode.data.jobTitleId || '')}
                                        </div>
                                    </Pills>
                                }
                                name={getFullName(satelliteNode.data)}
                            />

                            <div
                                className={'flex h-full p-2'}
                                data-testid={`remove-satellite-${satelliteNode.data?.id}`}
                            >
                                <Icon
                                    name={'essential-close-circle'}
                                    color={Colors.BASE_300}
                                    className={`${defaultHover}`}
                                    aria-label={`Remove ${getFullName(satelliteNode.data)}`}
                                    onClick={() => onRemove(satelliteNode.data?.id)}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};
